import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { Box, Checkbox } from "@mui/material"
import React, { useCallback, useEffect, useMemo } from "react"
import { useParams } from "react-router"

import { NoticeBoardStore } from "./store"
import { NoticeBoardStatusChip } from "./NoticeBoardStatusChip"

import { track } from "src/analytics/AnalyticsManager"

import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { GlobalStore } from "src/store"
import { NoticeBoardPostDetailModal } from "src/modals/notice-board-post-detail"
import { DataGridProTable } from "src/components/Table/DataGridPro"
import { ListPage } from "src/components/ListPage"
import { formatDateTime } from "src/lib/date"
import { reportError } from "src/lib/report"
import { SegmentPickerButton } from "src/components/SegmentPickerButton"
import { AccessTypeHeader } from "src/components/AccessTypeHeader"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import { MixpanelProperties } from "src/analytics/constants/properties"
import { Repository } from "src/types/channel"
import { IColumn, SortModel, FilterModel } from "src/types/data-grid-pro"
import {
    getFilterModelForRepository,
    getSortModelForRepository,
} from "src/lib/data-grid-pro"
import { truncateText } from "src/lib/text"

const View = observer(() => {
    const repository: Repository = "messages"
    const vstore = useStore(NoticeBoardStore)
    const gstore = useStore(GlobalStore)
    let { noticeBoardId } = useParams()
    const hiddenColumns = gstore.session.hasAccessToModule("ai_chatbot")
        ? []
        : ["includeInChatbot"]

    useEffect(() => {
        ;(async () => {
            await vstore.init(gstore.session.accessGroupId)
        })()

        return () => {
            vstore.dispose()
        }
    }, [vstore, gstore.session.accessGroupId, gstore.session.segmentIds])

    useEffect(() => {
        ;(async () => {
            if (noticeBoardId !== undefined) {
                await handleOpenPostClick(Number(noticeBoardId), "Edit")
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSearchChange = useCallback(
        (query: string) => vstore.posts.loadSearch(query),
        [vstore],
    )

    const statusValueOptions = useMemo(
        () => [
            {
                label: t`notice-board-view.status-chip-published-status`,
                value: "published",
            },
            {
                label: t`notice-board-view.status-chip-unpublished-status`,
                value: "unpublished",
            },
            {
                label: t`notice-board-view.status-chip-scheduled-status`,
                value: "scheduled",
            },
        ],
        [],
    )

    const handleNewPostClick = useCallback(() => {
        track("Notice Board | Create New")
        gstore.modals.open(() => <NoticeBoardPostDetailModal />, {
            hasPreview: true,
        })
    }, [gstore.modals])

    const handleSegmentChange = useCallback(
        (segments: number[]) => vstore.loadSegments(segments),
        [vstore],
    )

    const handleOpenPostClick = useCallback(
        (id: number, mode: string) => {
            gstore.modals.open(
                () => <NoticeBoardPostDetailModal id={id} mode={mode} />,
                {
                    hasPreview: true,
                },
            )
        },
        [gstore.modals],
    )

    const createOpenPostClickHandler = useCallback(
        (id: number, mode: string) => (event: React.MouseEvent<Element>) => {
            trackModuleEvent(`Notice Board | ${mode}`, {
                [MixpanelProperties.AccessGroupID]:
                    gstore.session.accessGroup?.id,
                [MixpanelProperties.AccessGroupName]:
                    gstore.session.accessGroup?.name,
                [MixpanelProperties.UserID]: gstore.session.user?.adminId,
            })
            event.preventDefault()
            handleOpenPostClick(id, mode)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleOpenPostClick],
    )

    const createDeletePostClickHandler = useCallback(
        (id: number) => async () => {
            try {
                await vstore.deletePost(id)
            } catch (e) {
                reportError(t`notice-board-view.failed-to-delete-post`, e)
            }
        },
        [vstore],
    )

    const createSavePinnedClickHandler = useCallback(
        (id: number) => async (_: unknown, pinned: boolean) => {
            await vstore.savePinnedStatus(id, pinned)
        },
        [vstore],
    )

    const header = useMemo(
        () => ({
            header: t`notice-board-view.header`,
            breadcrumbs: [t`notice-board-view.header`],
            createOptions: {
                item: t`notice-board-view.add-post`,
                onClick: handleNewPostClick,
            },
        }),
        [handleNewPostClick],
    )

    const filter = useMemo(
        () => ({
            query: vstore.posts.meta.search ?? "",
            onQueryChange: handleSearchChange,
            actions: (
                <>
                    <SegmentPickerButton
                        value={vstore.segments}
                        onChange={handleSegmentChange}
                    />
                </>
            ),
        }),
        [
            handleSearchChange,
            handleSegmentChange,
            vstore.posts.meta.search,
            vstore.segments,
        ],
    )

    // Keep the page mounted while querying so the filter popover stays open.
    // Only show the page-level spinner before the first data load.
    const pageIsLoading = !vstore.posts.meta.initialized
    const tableIsLoading = gstore.loading.is(
        NoticeBoardStore.LoadingKeys.pageLoad,
    )

    const columns: IColumn<{
        id: number
        adminTitle: string
        message: string
        pinned: boolean
        status: string
        includeInChatbot: boolean
        createdAt: Date | null
        receiverCount: number
        readCount: number
        sender: string
        accessType: string
    }>[] = [
        {
            field: "id",
            headerName: t`notice-board-view.messageId-header`,
            filterable: true,
            type: "number",
            minWidth: 100,
        },
        {
            field: "adminTitle",
            headerName: t`notice-board-view.admin-title-header`,
            type: "string",
            minWidth: 200,
            flex: 1.5,
            filterable: false,
            renderCell: (params) => (
                <Box
                    component="span"
                    title={params.value}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    {truncateText(params.value, 30)}
                </Box>
            ),
        },
        {
            field: "message",
            headerName: t`notice-board-view.post-header`,
            type: "string",
            minWidth: 200,
            flex: 2,
            filterable: false,
            renderCell: (params) => (
                <Box
                    component="span"
                    title={params.value}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    {truncateText(params.value, 30)}
                </Box>
            ),
        },
        {
            field: "pinned",
            headerName: t`notice-board-view.pinned-header`,
            type: "boolean",
            renderCell: (params) => (
                <Checkbox
                    checked={params.value === true}
                    color="info"
                    onClick={(event) => event.stopPropagation()}
                    onChange={createSavePinnedClickHandler(params.row.id)}
                    disabled={params.row.accessType === "READ"}
                />
            ),
            sortable: true,
            filterable: true,
        },
        {
            field: "status",
            headerName: t`notice-board-view.status-header`,
            filterable: true,
            type: "singleSelect",
            // @ts-ignore
            // Value options is available on one of the options for type of TColumn(GridSingleSelectColDef), but it's not reading here
            valueOptions: statusValueOptions,
            renderCell: (params) => (
                <NoticeBoardStatusChip status={params.value} />
            ),
        },
        {
            field: "includeInChatbot",
            headerName: t`notice-board-view.include-in-chatbot-header`,
            type: "boolean",
            filterable: true,
            // emulate hidden column by zero width if access missing
            minWidth: hiddenColumns.includes("includeInChatbot")
                ? 0
                : undefined,
            flex: hiddenColumns.includes("includeInChatbot") ? 0 : undefined,
            renderCell: (params) =>
                params.value === true
                    ? t`notice-board-view.include-in-chatbot-yes`
                    : t`notice-board-view.include-in-chatbot-no`,
        },
        {
            field: "createdAt",
            headerName: t`notice-board-view.created-header`,
            type: "date",
            renderCell: (params) =>
                params.value != null ? formatDateTime(params.value) : null,
            sortable: true,
            filterable: true,
        },
        {
            field: "receiverCount",
            headerName: t`notice-board-view.receiver-header`,
            type: "number",
            sortable: true,
            filterable: false,
        },
        {
            field: "readCount",
            headerName: t`notice-board-view.read-header`,
            type: "number",
            sortable: true,
            filterable: false,
        },
        {
            field: "sender",
            headerName: t`notice-board-view.sender-header`,
            type: "string",
            filterable: true,
        },
        {
            field: "accessType",
            headerName: t`notice-board-view.access-type`,
            filterable: false,
            renderCell: (params) => (
                <AccessTypeHeader accessType={params.value} />
            ),
        },
    ]

    const handleSortChange = useCallback(
        async (model: SortModel) => {
            const filter = getFilterModelForRepository(
                repository,
                gstore.session.dataGridFilterModel,
            )
            // search is handled by ListPage filter query as before
            await vstore.posts.loadAdvanceQuery({ filter, sort: model })
        },
        [gstore.session.dataGridFilterModel, vstore.posts],
    )

    const handleFilterChange = useCallback(
        async (model: FilterModel) => {
            const sort = getSortModelForRepository(
                repository,
                gstore.session.dataGridSortModel,
            )
            await vstore.posts.loadAdvanceQuery({ filter: model, sort })
        },
        [gstore.session.dataGridSortModel, vstore.posts],
    )

    return (
        <ListPage header={header} filter={filter} loading={pageIsLoading}>
            <DataGridProTable
                paginator={vstore.posts}
                data={vstore.posts.items}
                columns={columns}
                repository={repository}
                advancedOperations={{
                    pagination: "server",
                    filtering: "server",
                    sorting: "server",
                }}
                loading={tableIsLoading}
                onRowClickEvent={(params) =>
                    handleOpenPostClick(params.row.id, "Edit")
                }
                onSortChange={handleSortChange}
                onFilterChange={handleFilterChange}
                rowActionsRenderer={(item) => [
                    {
                        text: t`notice-board-view.copy-post`,
                        onClick: createOpenPostClickHandler(item.id, "Copy"),
                    },
                    {
                        text: t`notice-board-view.edit-post`,
                        onClick: createOpenPostClickHandler(item.id, "Edit"),
                    },
                    {
                        text: t`notice-board-view.delete-post`,
                        onClick: createDeletePostClickHandler(item.id),
                        destructive: true,
                    },
                ]}
            />
        </ListPage>
    )
})

export const NoticeBoardView = () => (
    <StoreProvider Store={NoticeBoardStore}>
        <View />
    </StoreProvider>
)
